import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

const btnSecondary = {
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  color: '#334155',
};
const btnPrimary = {
  ...btnSecondary,
  background: '#0f766e',
  borderColor: '#0f766e',
  color: '#fff',
  fontWeight: 600,
};

function fmtMh(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** MM/DD/YY — readable in UI and PNG export (no CSS writing-mode). */
function fmtDate(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '—';
  const [y, m, d] = ymd.split('-');
  return `${m}/${d}/${y.slice(2)}`;
}

/**
 * Year → Month → Day bands from Sunday week-starts.
 * Nemaska PDF uses vertical YYYY-MM-DD Sundays; we expose the same Sundays
 * as three readable layers (year span, month span, day-of-month).
 */
function buildCalendarBands(weeks) {
  const list = Array.isArray(weeks) ? weeks.filter((w) => /^\d{4}-\d{2}-\d{2}$/.test(w)) : [];
  const years = [];
  const months = [];
  const days = list.map((week) => {
    const [y, m, d] = week.split('-').map(Number);
    return {
      week,
      year: y,
      month: m,
      day: d,
      label: String(d).padStart(2, '0'),
    };
  });
  for (const cell of days) {
    const lastY = years[years.length - 1];
    if (lastY && lastY.year === cell.year) lastY.span += 1;
    else years.push({ year: cell.year, label: String(cell.year), span: 1 });
    const monthKey = `${cell.year}-${String(cell.month).padStart(2, '0')}`;
    const lastM = months[months.length - 1];
    if (lastM && lastM.key === monthKey) lastM.span += 1;
    else {
      months.push({
        key: monthKey,
        label: MONTH_SHORT[cell.month - 1] || monthKey,
        span: 1,
      });
    }
  }
  return { years, months, days };
}

const bandCellBase = {
  flexShrink: 0,
  textAlign: 'center',
  borderRight: '1px solid #93c5fd',
  borderBottom: '1px solid #93c5fd',
  boxSizing: 'border-box',
  writingMode: 'horizontal-tb',
  transform: 'none',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function HeaderGutterLabel({ width, label, sub }) {
  return (
    <div
      style={{
        width,
        flexShrink: 0,
        borderRight: '1px solid #93c5fd',
        borderBottom: '1px solid #93c5fd',
        background: '#eff6ff',
        padding: '2px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        boxSizing: 'border-box',
        color: '#1e3a8a',
        fontWeight: 800,
        fontSize: 10,
        letterSpacing: 0.2,
      }}
    >
      <span>{label}</span>
      {sub ? <span style={{ fontWeight: 600, color: '#64748b', fontSize: 9 }}>{sub}</span> : null}
    </div>
  );
}

/**
 * Three-layer calendar header matching the requested LBS layout:
 *   1) Year  2) Month  3) Day = Sunday week-start
 * (Nemaska PDF encodes the same Sundays as vertical YYYY-MM-DD.)
 */
function CalendarWeekHeader({ weeks, cellW, leftW, metaW }) {
  const bands = buildCalendarBands(weeks);
  const gutter = leftW + metaW;
  return (
    <div
      data-lbs-calendar-header="year-month-sunday"
      style={{ borderBottom: '2px solid #1e3a8a', fontWeight: 700, color: '#0f172a', background: '#dbeafe' }}
    >
      {/* Layer 1 — Year */}
      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 22 }}>
        <HeaderGutterLabel width={gutter} label="Year" />
        {bands.years.map((y) => (
          <div
            key={`y-${y.year}`}
            title={`Year ${y.year}`}
            style={{
              ...bandCellBase,
              width: y.span * cellW,
              minHeight: 22,
              background: '#bfdbfe',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 0.6,
              color: '#1e3a8a',
            }}
          >
            {y.label}
          </div>
        ))}
      </div>

      {/* Layer 2 — Month */}
      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 20 }}>
        <HeaderGutterLabel width={gutter} label="Month" />
        {bands.months.map((m) => (
          <div
            key={`m-${m.key}`}
            title={m.key}
            style={{
              ...bandCellBase,
              width: m.span * cellW,
              minHeight: 20,
              background: '#dbeafe',
              fontSize: 10,
              fontWeight: 800,
              color: '#1e40af',
            }}
          >
            {m.label}
          </div>
        ))}
      </div>

      {/* Layer 3 — Sunday day-of-month */}
      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 24, background: '#eff6ff' }}>
        <div
          style={{
            width: leftW,
            flexShrink: 0,
            padding: '4px 6px',
            borderRight: '1px solid #93c5fd',
            borderBottom: '1px solid #93c5fd',
            display: 'flex',
            alignItems: 'flex-end',
            fontSize: 10,
            fontWeight: 800,
            color: '#1e3a8a',
            boxSizing: 'border-box',
          }}
        >
          Location
        </div>
        <div
          style={{
            width: metaW,
            flexShrink: 0,
            padding: '4px 4px',
            borderRight: '1px solid #93c5fd',
            borderBottom: '1px solid #93c5fd',
            display: 'flex',
            gap: 6,
            alignItems: 'flex-end',
            fontSize: 10,
            fontWeight: 800,
            color: '#1e3a8a',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ width: 64 }}>Start</span>
          <span style={{ width: 64 }}>Finish</span>
          <span style={{ width: 44, textAlign: 'right' }}>MH</span>
        </div>
        {bands.days.map((d) => (
          <div
            key={d.week}
            data-week-label={d.week}
            title={`Sunday ${d.week}`}
            style={{
              ...bandCellBase,
              width: cellW,
              minHeight: 24,
              fontSize: 9,
              fontWeight: 800,
              background: '#eff6ff',
              color: '#0f172a',
            }}
          >
            {d.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Estimate / job Location-Based Schedule — Nemaska-style location × week grid
 * driven by live P6 (Current + dated XER revs), same pattern as Press Progress.
 */
const EstimateLbsOnePager = ({ estimate, estimateNumber: estimateNumberProp, jobNumber, onBack }) => {
  const { instance, accounts } = useMsal();
  const estimateNumber = estimateNumberProp || estimate?.estimate_number || estimate;
  const [data, setData] = useState(null);
  const [rev, setRev] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [present, setPresent] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const pageRef = useRef(null);

  const load = useCallback(async () => {
    if (!estimateNumber) return;
    try {
      setLoading(true);
      setError(null);
      const qs = rev ? `?rev=${encodeURIComponent(rev)}` : '';
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/estimate-lbs/${encodeURIComponent(estimateNumber)}${qs}`
      );
      const payload = res.data.data;
      setData(payload);
      if (!rev && payload?.selectedRev) setRev(String(payload.selectedRev));
    } catch (e) {
      console.error('Estimate LBS load failed:', e);
      setError(e.response?.data?.error || 'Failed to load LBS');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, estimateNumber, rev]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!present) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPresent(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [present]);

  const exportPng = async () => {
    const node = pageRef.current;
    if (!node) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      // Capture full scroll width. Headers are Year / Month / Sunday day — no
      // CSS writing-mode (html2canvas mangled those). Keep day cells as-is.
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        width: node.scrollWidth,
        height: node.scrollHeight,
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
        onclone: (_doc, cloned) => {
          cloned.style.overflow = 'visible';
          cloned.style.width = `${node.scrollWidth}px`;
          cloned.querySelectorAll('[data-date-label]').forEach((el) => {
            const label = el.getAttribute('data-date-label');
            if (label) el.textContent = label;
          });
        },
      });
      const a = document.createElement('a');
      const tag = (data?.revision?.projShortName || 'lbs').replace(/[^\w.-]+/g, '_');
      const who = String(estimateNumber || jobNumber || 'LBS').replace(/[^\w.-]+/g, '_');
      a.download = `${who}-LBS-${tag}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    } catch (e) {
      alert('PNG export failed — is html2canvas available?');
      console.error(e);
    }
  };

  if (!estimateNumber) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        Select an estimate (or open a job with an estimate mapping) to open its Location-Based Schedule.
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading LBS from P6…</div>;
  }

  if (error || !data?.available || data?.offline) {
    return (
      <div style={{ padding: '1rem 1.5rem' }}>
        {onBack ? <button onClick={onBack} style={btnSecondary}>← Back</button> : null}
        <div style={{ marginTop: 16, padding: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c' }}>
          {error || data?.reason || 'LBS not available for this estimate'}
        </div>
        {data?.offline && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#64748b', maxWidth: 560 }}>
            Needs a live connection to Primavera P6 SQL (<code>lgl24</code>). On a laptop run <code>npm run p6:tunnel</code>.
          </div>
        )}
        <button onClick={load} style={{ ...btnSecondary, marginTop: 12 }}>Retry</button>
      </div>
    );
  }

  const weeks = data.weeks || [];
  const cellW = Math.max(22, Math.min(30, Math.floor(960 / Math.max(weeks.length, 1))));
  const leftW = 220;
  const metaW = 200;

  const shell = {
    padding: present ? 12 : '0.75rem 1rem 1.25rem',
    background: present ? '#0f172a' : 'transparent',
    minHeight: present ? '100vh' : undefined,
    boxSizing: 'border-box',
  };

  return (
    <div style={shell}>
      {!present && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          {onBack ? <button onClick={onBack} style={btnSecondary}>← Back</button> : null}
          <h2 style={{ margin: 0, fontSize: 20 }}>Location-Based Schedule</h2>
          <span style={{ color: '#64748b', fontSize: 13 }}>
            {data.estimateNumber}
            {jobNumber ? ` · Job ${jobNumber}` : ''}
            {data.clientName ? ` · ${data.clientName}` : ''}
          </span>
          {(data.revisions || []).length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
              Rev
              <select
                value={rev || data.selectedRev || ''}
                onChange={(e) => setRev(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, maxWidth: 300 }}
              >
                {(data.revisions || []).map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </label>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowHelp(true)} title="Help" style={{ ...btnSecondary, padding: '7px 10px' }}>❓</button>
          <button onClick={load} style={btnSecondary}>↻ Refresh</button>
          <button onClick={exportPng} style={btnSecondary}>⬇ PNG</button>
          <button onClick={() => setPresent(true)} style={btnPrimary}>Present</button>
        </div>
      )}

      {!present && data.emptyDated?.length > 0 && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          P6 has empty dated rev(s): {data.emptyDated.map((d) => d.projShortName).join(', ')}.
          Import the schedule into those projects for them to appear as selectable revs.
        </div>
      )}

      {showHelp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 560, maxWidth: '94vw', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>❓ LBS Help</strong>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ListenButton compact guideId="estimate-lbs-help" title="Estimate LBS Help" accent="#0f766e" dark />
                <button onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 18, fontSize: 14, lineHeight: 1.55, color: '#334155' }}>
              <HelpWithVideoTour guideId="estimate-lbs-help" title="Estimate LBS Help" accent="#0f766e">
                <p><strong>Rev</strong> picks which P6 copy to show. Default is live <em>Current</em>. Dated XER copies stay in the list as history — when a new rev lands in P6 it shows up here automatically (same pattern as Press Progress on 75998).</p>
                <p>Each <strong>Pipe Rack</strong> is a location row. Week cells use Nemaska-style phase codes (ST / PA / TA / RS) split from Ironworker, Piping, and Electrical budgets. Program lanes above cover Engineering, Detailing, Steel Fab, and Pipe Fab.</p>
                <p>Week columns start on <strong>Sunday</strong>. The date header has three layers: <strong>Year</strong>, <strong>Month</strong>, then the Sunday day number. Enable LBS under <strong>Schedule options</strong> when you want this view.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {present && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, color: '#e2e8f0' }}>
          <span style={{ fontWeight: 700 }}>{data.title}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportPng} style={btnSecondary}>⬇ PNG</button>
            <button onClick={() => setPresent(false)} style={btnPrimary}>Exit (Esc)</button>
          </div>
        </div>
      )}

      <div
        ref={pageRef}
        style={{
          background: '#fff',
          border: '2px solid #0f172a',
          padding: '10px 10px 12px',
          boxShadow: present ? '0 8px 32px rgba(0,0,0,0.35)' : 'none',
          overflowX: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{data.title}</div>
          <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
            <strong>{data.revision?.label || data.revision?.projShortName}</strong>
            {data.horizon?.start ? <> · {fmtDate(data.horizon.start)} → {fmtDate(data.horizon.end)}</> : null}
            {data.totals ? <> · {data.totals.locations} locations · {fmtMh(data.totals.budgetMh)} bud MH</> : null}
          </div>
        </div>

        <div style={{ display: 'inline-block', minWidth: '100%' }}>
          <CalendarWeekHeader weeks={weeks} cellW={cellW} leftW={leftW} metaW={metaW} />

          {(data.program || []).map((row) => (
            <LbsRow
              key={`p-${row.id}`}
              label={row.label}
              subLabel={row.phase}
              start={row.start}
              finish={row.finish}
              mh={row.budgetMh}
              cells={row.cells}
              cellW={cellW}
              leftW={leftW}
              metaW={metaW}
              muted
            />
          ))}

          {(data.program || []).length > 0 && (
            <div style={{ height: 4, background: '#0f172a', margin: '2px 0' }} />
          )}

          {(data.locations || []).map((row, idx, arr) => {
            const prevBatch = idx > 0 ? arr[idx - 1].batch : null;
            const showBatch = row.batch && row.batch !== prevBatch;
            return (
              <React.Fragment key={row.id}>
                {showBatch && (
                  <div
                    style={{
                      display: 'flex',
                      background: '#e2e8f0',
                      fontSize: 10,
                      fontWeight: 800,
                      color: '#0f172a',
                      padding: '3px 6px',
                      borderBottom: '1px solid #94a3b8',
                    }}
                  >
                    {row.batch}
                  </div>
                )}
                <LbsRow
                  label={row.label}
                  subLabel={row.id}
                  start={row.start}
                  finish={row.finish}
                  mh={row.budgetMh}
                  cells={row.cells}
                  cellW={cellW}
                  leftW={leftW}
                  metaW={metaW}
                  title={`ST ${fmtMh(row.resources?.ST)} · PA ${fmtMh(row.resources?.PA)} · TA ${fmtMh(row.resources?.TA)}`}
                />
              </React.Fragment>
            );
          })}

          {(data.weeklyLoad || []).length > 0 && (
            <>
              <div style={{ height: 4, background: '#0f172a', margin: '2px 0' }} />
              <div style={{ display: 'flex', alignItems: 'stretch', fontSize: 9, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ width: leftW, flexShrink: 0, padding: '3px 6px', fontWeight: 700, borderRight: '1px solid #cbd5e1' }}>
                  Active racks / week
                </div>
                <div style={{ width: metaW, flexShrink: 0, borderRight: '1px solid #cbd5e1' }} />
                {(data.weeklyLoad || []).map((w) => (
                  <div
                    key={w.week}
                    data-week-label={`${fmtDate(w.week)} (${w.activeRacks})`}
                    style={{
                      width: cellW,
                      flexShrink: 0,
                      textAlign: 'center',
                      borderRight: '1px solid #f1f5f9',
                      background: w.activeRacks ? `rgba(15,118,110,${Math.min(0.15 + w.activeRacks * 0.08, 0.85)})` : '#fff',
                      fontWeight: 700,
                      color: '#0f172a',
                      padding: '2px 0',
                    }}
                    title={`${w.week}: ${w.activeRacks}`}
                  >
                    {w.activeRacks || ''}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 10, color: '#475569' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {(data.phases || []).map((p) => (
              <span key={p.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 12, height: 12, background: p.color, border: '1px solid #334155', display: 'inline-block' }} />
                <strong>{p.code}</strong> {p.label}
              </span>
            ))}
          </div>
          {(data.milestones || []).length > 0 && (
            <div style={{ maxWidth: 480 }}>
              <strong>Milestones:</strong>{' '}
              {data.milestones.map((m) => `${fmtDate(m.date)} ${m.label}`).join(' · ')}
            </div>
          )}
        </div>
        <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
          Live P6 · date header = Year / Month / Sunday day · enable via Schedule options
        </div>
      </div>
    </div>
  );
};

function LbsRow({
  label, subLabel, start, finish, mh, cells, cellW, leftW, metaW, muted, title,
}) {
  return (
    <div
      title={title}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid #e2e8f0',
        background: muted ? '#f8fafc' : '#fff',
        fontSize: 10,
        minHeight: 22,
      }}
    >
      <div style={{ width: leftW, flexShrink: 0, padding: '2px 6px', borderRight: '1px solid #cbd5e1', overflow: 'hidden' }}>
        <div style={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
        {subLabel ? <div style={{ color: '#64748b', fontSize: 9 }}>{subLabel}</div> : null}
      </div>
      <div style={{ width: metaW, flexShrink: 0, padding: '2px 4px', borderRight: '1px solid #cbd5e1', display: 'flex', gap: 6, alignItems: 'center', fontVariantNumeric: 'tabular-nums', color: '#334155' }}>
        <span style={{ width: 64 }} data-date-label={fmtDate(start)}>{fmtDate(start)}</span>
        <span style={{ width: 64 }} data-date-label={fmtDate(finish)}>{fmtDate(finish)}</span>
        <span style={{ width: 44, textAlign: 'right' }}>{mh ? fmtMh(mh) : ''}</span>
      </div>
      {(cells || []).map((c) => (
        <div
          key={c.week}
          title={c.active ? `${fmtDate(c.week)} ${c.phase || ''}` : fmtDate(c.week)}
          style={{
            width: cellW,
            flexShrink: 0,
            borderRight: '1px solid #f1f5f9',
            background: c.active ? (c.color || '#94a3b8') : 'transparent',
            color: '#fff',
            fontWeight: 800,
            fontSize: 8,
            textAlign: 'center',
            lineHeight: '22px',
            textShadow: c.active ? '0 0 2px rgba(0,0,0,0.45)' : undefined,
          }}
        >
          {c.active ? c.phase : ''}
        </div>
      ))}
    </div>
  );
}

export default EstimateLbsOnePager;
